import {AfterViewInit, Component, OnInit, ViewChild} from '@angular/core';
import {DictionaryService, FieldProcessService, TenantService} from '@twpub/core/services';
import {Dictionary, DictionaryInfo, FieldDataRow, FieldKey, TenantConfiguration} from '@twpub/core/models';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource
} from '@angular/material/table';
import {MatSort, MatSortHeader} from '@angular/material/sort';
import {MatDialog} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {ConfirmDialogComponent, DialogData} from '../confirm-dialog/confirm-dialog.component';
import {forkJoin} from 'rxjs';
import {NGXLogger} from 'ngx-logger';

import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {MatOption, MatSelect} from '@angular/material/select';
import {MatCheckbox} from '@angular/material/checkbox';
import {MatButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';

@Component({
  selector: 'pub-searchable-fields-panel',
  templateUrl: './searchable-fields-panel.component.html',
  styleUrls: ['./searchable-fields-panel.component.scss'],
  standalone: true,
  imports: [MatProgressSpinner, MatFormField, MatLabel, MatSelect, MatOption, MatTable, MatSort, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatSortHeader, MatCellDef, MatCell, MatCheckbox, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, MatButton, MatIcon]
})
export class SearchableFieldsPanelComponent implements OnInit, AfterViewInit {
  displayedColumns: string[] = ['label', 'level', 'dictionaryName', 'searchable'];
  dataSource = new MatTableDataSource<FieldDataRow>([]);
  dictionaries: Dictionary[] = [];
  tenantConfig?: TenantConfiguration;
  isLoading = true;

  // Properties for dictionary filtering
  selectedDictionaryId: number | null = null;
  allRows: FieldDataRow[] = [];

  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private logger: NGXLogger,
    private dictionaryService: DictionaryService,
    private fieldProcessService: FieldProcessService,
    private tenantService: TenantService,
    public dialog: MatDialog,
    private router: Router
  ) {
  }

  ngOnInit(): void {
    this.loadData();
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
  }

  loadData(): void {
    this.isLoading = true;

    // Clear dictionary cache to ensure we get fresh data with populated fieldKeys
    this.dictionaryService.clearCache();

    forkJoin({
      dictionaries: this.dictionaryService.getDictionaries(true),
      configuration: this.tenantService.getCurrentTenant()
    }).subscribe({
      next: (result: { dictionaries: Dictionary[], configuration: TenantConfiguration }) => {
        this.dictionaries = result.dictionaries;
        this.tenantConfig = result.configuration;
        // Ensure searchableFieldKeys is always an array
        if (!this.tenantConfig.searchableFieldKeys) {
          this.tenantConfig.searchableFieldKeys = [];
        }
        this.processData();
        this.isLoading = false;
      },
      error: err => {
        this.logger.error('Error loading data', err);
        this.isLoading = false;
      }
    });
  }

  processData(): void {
    const fieldMap = this.fieldProcessService.getFieldMap(this.dictionaries, (fk) => this.isFieldSearchable(fk));

    // Convert map to array and sort
    const rows = Array.from(fieldMap.values());

    // Store all rows for filtering
    this.allRows = rows;
    this.dataSource.data = rows;
  }

  isFieldSearchable(fieldKey: FieldKey): boolean {
    if (!this.tenantConfig?.searchableFieldKeys) return false;

    return this.tenantConfig.searchableFieldKeys.some(
      f => f.fieldOid === fieldKey.fieldOid && f.fieldLevel === fieldKey.fieldLevel
    );
  }

  toggleSearchable(row: FieldDataRow): void {
    row.searchable = !row.searchable;
  }

  applyChanges(): void {
    if (!this.tenantConfig) return;

    const searchableFields: FieldKey[] = [];
    this.allRows
      .filter(row => row.searchable)
      .forEach(row => {
        row.dictionaries.forEach((dict: DictionaryInfo) => {
          searchableFields.push(dict.fieldKey);
        });
      });

    this.tenantConfig.searchableFieldKeys = searchableFields;

    // Step 1: Dry run to get counts
    this.tenantService.updateConfiguration(this.tenantConfig, true).subscribe({
      next: (result: { concepts: number, terms: number }) => {
        const longRun = result.concepts > 1000 || result.terms > 1000;
        if (longRun) {
          // Step 2: Show confirm dialog
          const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
              message: [
                'Reindexing is needed and can take a few minutes. If more fields should be searchable you can add them now.',
                'Do you want to start reindexing?'
              ],
              confirmLabel: 'Start Reindexing',
              cancelLabel: 'Continue Editing'
            } as DialogData
          });

          dialogRef.afterClosed().subscribe(confirmed => {
            if (confirmed) {
              this.startReindexing(longRun);
            }
          });
        } else {
          // Below threshold, start reindexing directly
          this.startReindexing(longRun);
        }
      },
      error: err => {
        this.logger.error('Error during dry run of configuration update', err);
      }
    });
  }

  private startReindexing(longRun: boolean): void {
    this.tenantService.updateConfiguration(this.tenantConfig!, false).subscribe({
      next: () => {
        if (longRun) {
          // Step 3: Show info dialog
          const infoDialog = this.dialog.open(ConfirmDialogComponent, {
            data: {
              message: [
                'Reindexing is performed in the background. You can continue to work.'
              ],
              confirmLabel: 'OK'
            } as DialogData
          });

          infoDialog.afterClosed().subscribe(() => {
            // Step 4: Redirect to admin/home
            this.router.navigate(['/admin/home']);
          });
        } else {
          // No long run, just redirect to admin/home
          this.router.navigate(['/admin/home']);
        }
      },
      error: err => {
        this.logger.error('Error starting reindexing', err);
      }
    });
  }

  // Filter method for dictionary selection
  filterByDictionary(dictionaryId: number | null): void {
    this.selectedDictionaryId = dictionaryId;

    if (dictionaryId === null) {
      // Show all dictionaries
      this.dataSource.data = this.allRows;
    } else {
      // Filter to show only fields that include the selected dictionary
      this.dataSource.data = this.allRows.filter(row =>
        row.dictionaries.some((dict: DictionaryInfo) => dict.id === dictionaryId)
      );
    }
  }
}
